import React, { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from 'react';
import type { Cart, CartItem } from '../types/cart';
import { cartStorage } from '../utils/cartStorage';
import { productsAPI } from '../api/products';
import { apiClient } from '../api/client';
import { toast } from 'react-hot-toast';

interface CartContextType {
  cart: Cart;
  isLoading: boolean;
  addItem: (item: Omit<CartItem, 'id' | 'max_quantity' | 'available_quantity'>) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  validateCart: () => Promise<{ valid: boolean; errors: string[] }>;
  refreshStock: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<Cart>(cartStorage.getCart());
  const [isLoading, setIsLoading] = useState(false);

  const fetchAvailableQuantities = useCallback(async (variantIds: number[]): Promise<Record<string, number>> => {
    if (variantIds.length === 0) return {};
    
    try {
      const response = await productsAPI.getAvailableStock(variantIds.join(','));
      if (response.error) throw new Error(response.error);
      return response.data || {};
    } catch (error) {
      console.error('Failed to fetch available quantities:', error);
      return {};
    }
  }, []);

  const updateCartItemsWithStock = useCallback(async (cartItems: CartItem[]): Promise<CartItem[]> => {
    if (cartItems.length === 0) return [];

    const variantIds = cartItems.map(item => item.product_variant_id);
    const availableQuantities = await fetchAvailableQuantities(variantIds);

    return cartItems.map(item => ({
      ...item,
      // max_quantity: availableQuantities[item.product_variant_id.toString()] || 0,
      available_quantity: availableQuantities[item.product_variant_id.toString()] || 0
    }));
  }, [fetchAvailableQuantities]);

  const checkStockAvailability = useCallback(async (productVariantId: number, requestedQuantity: number): Promise<number> => {
    try {
      const response = await fetchAvailableQuantities([productVariantId]);
      const availableQuantity = response[productVariantId.toString()] || 0;
      return Math.min(requestedQuantity, availableQuantity);
    } catch (error) {
      console.error('Failed to check stock:', error);
      return 0;
    }
  }, [fetchAvailableQuantities]);

  const refreshStock = useCallback(async () => {
    if (cart.items.length === 0) return;

    setIsLoading(true);
    try {
      // 1. Fetch fresh stock from backend
      const variantIds = cart.items.map(item => item.product_variant_id).join(',');
      const response = await apiClient.get(`/store/stock/available/?variant_ids=${variantIds}`);

      // 2. Update items with real-time stock (quantity - reserved_quantity)
      const updatedItems = cart.items.map(item => ({
        ...item,
        available_quantity: response.data[item.product_variant_id.toString()] || 0
      }));

      // 3. Recalculate totals with updated items
      const totals = cartStorage.calculateTotals(updatedItems);

      // 4. Update cart state atomically
      setCart({
        items: updatedItems,
        ...totals,
        last_updated: new Date().toISOString(),
      });

    } catch (error) {
      console.error('Failed to refresh stock:', error);
      // Optionally: Fall back to cached values or show error
    } finally {
      setIsLoading(false);
    }
  }, [cart.items, updateCartItemsWithStock]); // Keep your existing updateCartItemsWithStock if needed for other logic

  useEffect(() => {
    const loadCartWithStock = async () => {
      const savedCart = cartStorage.getCart();

      if (savedCart.items.length > 0) {
        const updatedItems = await updateCartItemsWithStock(savedCart.items);
        const totals = cartStorage.calculateTotals(updatedItems);

        setCart({
          items: updatedItems,
          ...totals,
          last_updated: new Date().toISOString(),
        });
      } else {
        setCart(savedCart);
      }
    };

    loadCartWithStock();
  }, [updateCartItemsWithStock]);

  useEffect(() => {
    cartStorage.saveCart(cart);
  }, [cart]);

  useEffect(() => {
    if (cart.items.length === 0) return;

    const interval = setInterval(() => {
      refreshStock();
    }, 120000);

    return () => clearInterval(interval);
  }, [cart.items.length, refreshStock]);

  const addItem = async (newItem: Omit<CartItem, 'id' | 'max_quantity' | 'available_quantity'>) => {
    setIsLoading(true);

    try {
      const itemId = `variant_${newItem.product_variant_id}`;
      const existingItemIndex = cart.items.findIndex(item => item.id === itemId);

      if (existingItemIndex >= 0) {
        const existingItem = cart.items[existingItemIndex];
        const newQuantity = existingItem.quantity + newItem.quantity;
        const availableQuantity = await checkStockAvailability(newItem.product_variant_id, newQuantity);

        if (availableQuantity === 0) throw new Error('Insufficient stock available');

        const updatedItems = [...cart.items];
        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: availableQuantity,
          vendor_id: newItem.vendor_id || existingItem.vendor_id,
          vendor_name: newItem.vendor_name || existingItem.vendor_name,
          additional_images: newItem.additional_images.length > 0 ? newItem.additional_images : existingItem.additional_images,
          // max_quantity: availableQuantity,
          available_quantity: availableQuantity,
        };

        const totals = cartStorage.calculateTotals(updatedItems);
        setCart({ items: updatedItems, ...totals, last_updated: new Date().toISOString() });
      } else {
        const availableQuantity = await checkStockAvailability(newItem.product_variant_id, newItem.quantity);
        if (availableQuantity === 0) throw new Error('Insufficient stock available');

        const item: CartItem = {
          ...newItem,
          id: itemId,
          quantity: availableQuantity,
          // max_quantity: availableQuantity,
          available_quantity: availableQuantity,
        };

        const updatedItems = [...cart.items, item];
        const totals = cartStorage.calculateTotals(updatedItems);
        setCart({ items: updatedItems, ...totals, last_updated: new Date().toISOString() });
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to add item to cart');
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    setIsLoading(true);

    try {
      const itemIndex = cart.items.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return;

      const item = cart.items[itemIndex];
      if (quantity < 1) {
        removeItem(itemId);
        return;
      }

      // 1. Get actual available stock from backend
      const stockResponse = await apiClient.get(
        `/store/stock/available/?variant_ids=${item.product_variant_id}`
      );
      const availableStock = stockResponse.data[item.product_variant_id.toString()] || 0;
      // Show toast if at max
      if (quantity >= availableStock && availableStock > 0) {
        toast.success(`Maximum available quantity (${availableStock}) selected`, {
          icon: '📦',
          duration: 3000,
        });
        quantity = availableStock; // Cap it
      }
      // 2. Validate quantity against REAL stock
      if (quantity > availableStock) {
        throw new Error(`Only ${availableStock} available in stock`);
      }

      // 3. Update item with correct stock info
      const updatedItems = [...cart.items];
      updatedItems[itemIndex] = {
        ...item,
        quantity: quantity, // User's requested quantity
        // max_quantity: availableStock, // True maximum available
        available_quantity: availableStock, // True current stock
      };

      const totals = cartStorage.calculateTotals(updatedItems);
      setCart({ items: updatedItems, ...totals, last_updated: new Date().toISOString() });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update quantity');
    } finally {
      setIsLoading(false);
    }
  };

  const removeItem = (itemId: string) => {
    const updatedItems = cart.items.filter(item => item.id !== itemId);
    const totals = cartStorage.calculateTotals(updatedItems);
    setCart({ items: updatedItems, ...totals, last_updated: new Date().toISOString() });
  };

  const clearCart = () => {
    setCart({
      items: [],
      total_items: 0,
      subtotal: 0,
      last_updated: new Date().toISOString(),
    });
    cartStorage.clearCart();
  };

  const validateCart = async (): Promise<{ valid: boolean; errors: string[] }> => {
    const errors: string[] = [];

    if (cart.items.length === 0) {
      errors.push('Your cart is empty');
      return { valid: false, errors };
    }

    await refreshStock();

    for (const item of cart.items) {
      if (item.quantity > item.available_quantity) {
        if (item.available_quantity === 0) {
          errors.push(`${item.product_title} - ${item.variant_name} is out of stock`);
        } else {
          errors.push(`${item.product_title} - ${item.variant_name}: only ${item.available_quantity} available`);
        }
      }
    }

    return { valid: errors.length === 0, errors };
  };

  return (
    <CartContext.Provider value={{
      cart,
      isLoading,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      validateCart,
      refreshStock,
    }}>
      {children}
    </CartContext.Provider>
  );
};